import {
  Component,
  OnInit,
  ViewChild,
  ElementRef,
  inject,
  signal,
  computed,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef,
  input,
  Injectable,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Logger } from '../../../core/logger.service';
import { SubscriptionDto } from '../subscription-dto';
import { AccessConfigService } from '../../access-config.service';
import { ApiService } from '../../../core/api/api.service';
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from '@angular/material/paginator';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from '../../../core/authentication/authentication.service';
import { ConfirmationService } from '../../../shared/dialog/confirmation.service';
import { extract } from '../../../core/i18n.service';
import { OrderConfigComponent } from './order-config/order-config.component';
import { MessageBoxService } from '../../../shared/dialog/messagebox.service';

import { OrderDto } from '../order-dto';
import { BreakpointObserver, BreakpointState, Breakpoints } from '@angular/cdk/layout';
import { NgClass } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatInputModule } from '@angular/material/input';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatDividerModule } from '@angular/material/divider';
import { LoaderService } from '../../../shared/loader/loader.service';
import { FirebaseAuthService } from '../../../core/firebase/firebase-auth.service';

import { ModuleStateService } from '../../../module-state.service';
import { MatomoTracker } from 'ngx-matomo-client';
import { Router } from '@angular/router';
import { BuyCreditsMessage } from '../../../../../../commondata/messages/messages-dto';
import { ModuleMessageService } from '../../../core/module-message.service';
import { environment } from '../../../../../../commondata/environments/environment';
import { OrderComponent } from '../order/order.component';
import { Subject } from 'rxjs';

const logger: Logger = new Logger('order-list');

export enum OrderFilterType {
  ALL = 'filter-all',
  SINGLE = 'filter-single',
  MULTIPLE = 'filter-multiple',
  DOMAIN = 'filter-domain',
  DEMO = 'filter-demo',
}

@Injectable()
export class SMXAdminOrderPaginatorIntl extends MatPaginatorIntl {
  override changes = new Subject<void>();

  constructor(private translateService: TranslateService) {
    super();
    this._translateLabels();

    this.translateService.onLangChange.subscribe(() => {
      this._translateLabels();
      this.changes.next();
    });
  }

  private _translateLabels() {
    this.itemsPerPageLabel = this.translateService.instant('ORDER_PAGINATOR.ITEMS_PER_PAGE');
    this.nextPageLabel = this.translateService.instant('ORDER_PAGINATOR.NEXT_PAGE');
    this.previousPageLabel = this.translateService.instant('ORDER_PAGINATOR.PREVIOUS_PAGE');
    this.firstPageLabel = this.translateService.instant('ORDER_PAGINATOR.FIRST_PAGE');
    this.lastPageLabel = this.translateService.instant('ORDER_PAGINATOR.LAST_PAGE');

    this.getRangeLabel = (page: number, pageSize: number, length: number): string => {
      if (length === 0) {
        return this.translateService.instant('ORDER_PAGINATOR.NO_FILES');
      }

      const totalPages = Math.ceil(length / pageSize);
      const currentPageDisplay = page + 1;

      return this.translateService.instant('ORDER_PAGINATOR.RANGE_PAGE_WITH_PAGES', {
        current: currentPageDisplay,
        totalPages: totalPages,
        totalFiles: length,
      });
    };
  }
}

@Component({
  selector: 'app-order-list',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [{ provide: MatPaginatorIntl, useClass: SMXAdminOrderPaginatorIntl }],
  imports: [
    NgClass,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatPaginatorModule,
    MatExpansionModule,
    MatInputModule,
    MatCardModule,
    MatTooltipModule,
    MatSelectModule,
    MatDividerModule,
    TranslateModule,
    OrderComponent,
  ],
  templateUrl: './order-list.component.html',
  styleUrls: ['./order-list.component.scss']
})
export class OrderListComponent implements OnInit {
  public OrderFilterType = OrderFilterType;

  openFirstElement = input<boolean>(false);
  enabledDetail = input<boolean>(false);

  @ViewChild(MatPaginator, { static: false }) paginator: MatPaginator | undefined;
  @ViewChild('orderForm') orderForm: OrderConfigComponent | undefined;
  @ViewChild('searchtext') searchText: ElementRef | undefined;
  @ViewChildren(OrderComponent) orderComponents!: QueryList<OrderComponent>;

  public searchForm: FormGroup = new FormGroup({});

  public length = signal(1);
  public pageIndex = signal(1);
  public pageSize = signal(10);
  public searchedText = signal('');
  public isLoaded = signal(false);

  public compressed = signal(false);
  public medium = signal(false);
  public minimumState = signal(false);
  public expanded = signal(true);
  activeOrderFilter = signal(OrderFilterType.ALL);

  public subscriptions = signal<SubscriptionDto[]>([]);
  public activeSubscriptions = computed(() => {
    /*return this._applyTypeFilter(this.subscriptions()).filter(
      (sub) => !sub.isExpired && !sub.isSuspended,
    );*/

    return this.subscriptions().filter((sub) => !sub.isExpired && !sub.isSuspended);
  });

  public expiredSubscriptions = computed(() => {
    /*return this._applyTypeFilter(this.subscriptions()).filter(
      (sub) => sub.isExpired || sub.isSuspended,
    );*/
    return this.subscriptions().filter((sub) => sub.isExpired || sub.isSuspended);
  });

  public noList = computed(
    () => this.activeSubscriptions().length === 0 && this.expiredSubscriptions().length === 0,
  );

  // Metodo helper per notificare il figlio specifico
  private notifyChildComponent(subscriptionId: string) {
    const child = this.orderComponents?.find(
      (comp) => comp.subscription().order.subscriptionId === subscriptionId
    );
    if (child) {
      child.markForCheck();
    }
  }

  private _applyTypeFilter(all: SubscriptionDto[]): SubscriptionDto[] {
    switch (this.activeOrderFilter()) {
      case OrderFilterType.SINGLE:
        return all.filter((sub) => sub.order.licenseType === 's');
      case OrderFilterType.MULTIPLE:
        return all.filter((sub) => sub.order.licenseType === 'm');
      case OrderFilterType.DOMAIN:
        return all.filter((sub) => sub.order.licenseType === 'd');
      case OrderFilterType.DEMO:
        return all.filter(
          (sub) => sub.order.origin === 'demo' && sub.order.subscriptionId.startsWith('demo-'),
        );
      default:
        return all;
    }
  }

  private role = signal<string | undefined>(undefined);
  public isOperator = computed(() => {
    const r = this.role();
    return r === 'admin' || r === 'anastasis' || r === 'aid' || r === 'spain';
  });
  public isAdmin = computed(() => this.role() === 'admin');
  public isAnastasisOperator = computed(
    () => this.role() === 'anastasis' || this.role() === 'admin',
  );
  public canSendMail = computed(() => this.isAnastasisOperator());
  public canEditOrder = computed(() => this.isAnastasisOperator());
  public canDeleteSubscription = computed(() => this.isAdmin());

  public hasActiveSubscriptions = computed(() =>
    this.subscriptions().some((sub) => !sub.isExpired && !sub.isSuspended && !sub.order.TBD),
  );
  public hasTBDSubscriptions = computed(() =>
    this.subscriptions().some((sub) => sub.order.TBD === true),
  );
  public hasInvalidSubscriptions = computed(() =>
    this.subscriptions().some((sub) => sub.isExpired || sub.isSuspended),
  );
  public hasRedirectSubscriptions = computed(() =>
    this.subscriptions().some(
      (sub) => sub.order.redirectId !== null && sub.order.redirectId !== '',
    ),
  );

  private oldAiCredits = -1;

  router = inject(Router);
  loaderService = inject(LoaderService);
  isLoading = this.loaderService.isLoading;
  formBuilder = inject(FormBuilder);
  private accessConfigService = inject(AccessConfigService);
  apiService = inject(ApiService);
  firebaseAuthService = inject(FirebaseAuthService);
  breakpointObserver = inject(BreakpointObserver);
  private translateService = inject(TranslateService);
  private authenticationService = inject(AuthenticationService);
  private confirmationService = inject(ConfirmationService);
  private messageBoxService = inject(MessageBoxService);
  private moduleStateService = inject(ModuleStateService);
  private tracker = inject(MatomoTracker);
  private moduleMessageService = inject(ModuleMessageService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    this.breakpointObserver
      .observe(['(max-width:400px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((state: BreakpointState) => {
        this.minimumState.set(state.matches);
        this.compressed.set(state.matches);
        this.cdr.markForCheck();
      });

    this.breakpointObserver
      .observe(['(max-width:950px)'])
      .pipe(takeUntilDestroyed())
      .subscribe((state: BreakpointState) => {
        this.medium.set(state.matches);
        this.cdr.markForCheck();
      });

    this.breakpointObserver
      .observe([Breakpoints.XSmall])
      .pipe(takeUntilDestroyed())
      .subscribe((state: BreakpointState) => {
        this.compressed.set(state.matches);
        this.cdr.markForCheck();
      });
  }

  ngOnInit() {
    this.isLoaded.set(false);
    this.pageSize.set(10);
    this.searchForm = this.formBuilder.group({ searchText: [''] });

    if (this.accessConfigService.pageEvent()) {
      const pe = this.accessConfigService.pageEvent();
      if (pe) this.pageIndex.set(pe.pageIndex);
    } else {
      this.pageIndex.set(0);
    }

    const currentRole = this.authenticationService.credentials?.role;
    this.role.set(currentRole);

    if (!this.isOperator()) {
      if (this.firebaseAuthService.currentUser) {
        setTimeout(() => this.loadData(''));
      } else {
        this.firebaseAuthService.changedUser
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((user) => {
            if (user !== null) this.loadData('');
          });
      }
    } else {
      const search = localStorage.getItem('order-list-search')?.toString() || '';
      this.pageIndex.set(Number(localStorage.getItem('order-list-pageIndex')) || 0);
      this.pageSize.set(Number(localStorage.getItem('order-list-pageSize')) || 10);
      this.searchForm?.get('searchText')?.setValue(search !== '*' ? search : '');
      this.activeOrderFilter.set(
        (localStorage.getItem('order-list-filter') as OrderFilterType) || OrderFilterType.ALL,
      );
      this.loadData(search !== '*' ? search : '');
    }

    setTimeout(() => {
      this.searchText?.nativeElement.focus();
    }, 0);
  }

  loadData(search: string) {
    this.loaderService.startLoading('generic', 'PLEASE_WAIT');
    localStorage.setItem('order-list-search', search);
    localStorage.setItem('order-list-pageIndex', this.pageIndex().toString());
    localStorage.setItem('order-list-pageSize', this.pageSize().toString());
    localStorage.setItem('order-list-filter', this.activeOrderFilter().toString());

    this.accessConfigService.pageEvent.set(undefined);

    this.accessConfigService
      .searchSubscriptions(
        search,
        true,
        this.pageIndex() + 1,
        this.pageSize(),
        'expiryDate',
        false,
        this.activeOrderFilter(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (subs: any) => {
          this.length.set(subs.params.subsCount || 0);
          const pageEvent = this.accessConfigService.pageEvent();
          if (pageEvent) {
            this.pageIndex.set(pageEvent.pageIndex);
          }
          setTimeout(() => {
            if (this.paginator) this.paginator.pageIndex = this.pageIndex();
          }, 100);

          if (subs && subs.subs) {
            // Sostituito il ciclo con un map() ottimizzato
            const mappedSubs = subs.subs.map((s: any) => {
              const sub = new SubscriptionDto(s);
              sub.findsDescription = this.getFindsDescription(s.finds);
              this.getOrderStatus(sub);
              return sub;
            });

            this.subscriptions.set(mappedSubs);
            this.loaderService.stopLoading('generic');
            this.searchedText.set(search);
            this.isLoaded.set(true);

            if (this.openFirstElement() && mappedSubs.length > 0) {
              this.panelOpenState(mappedSubs[0], true);
            }
          } else {
            this.subscriptions.set([]);
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          console.log(error);
          this.loaderService.stopLoading('generic');
          this.cdr.markForCheck();
        },
      });
  }

  public panelOpenState(subscription: SubscriptionDto, open: boolean) {
    // --- 1. Gestione Scansioni OCR Math ---
    if (!subscription.order.isOcrMathScansLeftDone) {
      subscription.order.ocrMathScansLeft = this.translateService.instant('CALCULATING');
    }
    subscription.panelOpenState = open;

    if (open && !subscription.order.isOcrMathScansLeftDone) {
      this.apiService
        .getOcrMathPixScansLeft(subscription.order.subscriptionId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (_result: any) => {
            subscription.order.ocrMathScansLeft = _result.result.scansLeft;
            subscription.order.isOcrMathScansLeftDone = true;

            // Notifica sia il padre che il componente figlio
            this.cdr.markForCheck();
            this.notifyChildComponent(subscription.order.subscriptionId);
          },
          error: (error: any) => {
            console.log(error);
            subscription.order.ocrMathScansLeft = this.translateService.instant('ERROR');

            this.cdr.markForCheck();
            this.notifyChildComponent(subscription.order.subscriptionId);
          },
        });
    }

    // --- 2. Gestione Crediti AI ---
    let creditType = '';
    if (open && subscription.order.aiCredits === -1) {
      if (subscription.order.licenseType === 's') {
        if (subscription.access && subscription.access.admin && subscription.access.admin.eMail) {
          creditType = 'user';
        } else {
          creditType = 'subscription';
        }
      } else {
        creditType = 'subscription';
      }

      if (creditType === 'user') {
        this.apiService
          .getUsersByEmails(
            this.authenticationService.getUserIdentifier(),
            [subscription.access.admin.eMail],
            subscription.order.subscriptionId,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (result: any) => {
              console.log(`getUsersByEmails: ${JSON.stringify(result)}`);
              subscription.order.aiCredits =
                result?.data?.length > 0 && result.data[0].saldo.length > 0
                  ? result.data[0].saldo[0].saldo
                  : 0;
              const child = this.orderComponents?.find(
                (comp) => comp.subscription().order.subscriptionId === subscription.order.subscriptionId
              );
              if (child) {
                child.oldAiCredits = subscription.order.aiCredits;
              }
              this.cdr.markForCheck();
              this.notifyChildComponent(subscription.order.subscriptionId);
            },
            error: (error: any) => {
              console.log(error);
              subscription.order.aiCredits = 0;
              this.cdr.markForCheck();
              this.notifyChildComponent(subscription.order.subscriptionId);
            },
          });
      } else if (creditType === 'subscription') {
        this.apiService
          .getSubscriptionCredits(
            this.authenticationService.getUserIdentifier(),
            subscription.order.subscriptionId,
          )
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: (result: any) => {
              console.log(`getSubscriptionCredits: ${JSON.stringify(result)}`);
              subscription.order.aiCredits = result?.data?.saldo || 0;
              const child = this.orderComponents?.find(
                (comp) => comp.subscription().order.subscriptionId === subscription.order.subscriptionId
              );
              if (child) {
                child.oldAiCredits = subscription.order.aiCredits;
              }
              this.cdr.markForCheck();
              this.notifyChildComponent(subscription.order.subscriptionId);
            },
            error: (error: any) => {
              console.log(error);
              subscription.order.aiCredits = 0;
              this.cdr.markForCheck();
              this.notifyChildComponent(subscription.order.subscriptionId);
            },
          });
      } else {
        subscription.order.aiCredits = 0;
        this.cdr.markForCheck();
        this.notifyChildComponent(subscription.order.subscriptionId);
      }
    }
  }

  public changePage(event: PageEvent) {
    this.pageIndex.set(event.pageIndex);
    this.accessConfigService.pageEvent.set(event);

    const textSearch = this.searchForm?.get('searchText')?.value;
    this.loadData(textSearch);
  }

  public reloadData() {
    const textSearch = this.searchForm?.get('searchText')?.value;
    this.loadData(textSearch);
  }

  public getOrderStatus(subscription: SubscriptionDto) {
    let status = '?';
    if (subscription) {
      subscription.isSuspended = false;
      subscription.isExpired = false;
      if (subscription.order.suspended === true) {
        status = this.translateService.instant('ACCESSCONFIG_ORDER_SUSPENDED');
        subscription.isSuspended = true;
      } else {
        const now = new Date().toISOString().slice(0, 10);
        if (now > subscription.order.dbExpireDate) {
          status = this.translateService.instant('ACCESSCONFIG_ORDER_EXPIRED');
          subscription.isExpired = true;
        } else {
          status = this.translateService.instant('ACCESSCONFIG_ORDER_ACTIVE');
        }
      }
    }
    return status;
  }
  // -----------------------------------------------------------------

  configAccess(sub: SubscriptionDto) {
    if (this.enabledDetail() && !sub.order.suspended && !sub.isExpired /*|| this.isOperator()*/) {
      let url = '';
      if (sub.order.licenseType === 's') {
        url += `single-user-config/${sub.order.subscriptionId}/${sub.order.randomId}?operator=${this.isOperator()}`;
      } else {
        url += `access-config/${sub.order.subscriptionId}/${sub.order.randomId}?operator=${this.isOperator()}`;
      }
      localStorage.setItem('backUrl', window.location.pathname);
      this.router.navigate([url]);
    } else if (sub.order.suspended) {
      const msg = this.translateService.instant('ORDER-SUSPENDED');
      this.messageBoxService.showTextMessage(
        this.messageBoxService.MODE_TYPE.OK,
        this.translateService.instant('WARNING'),
        msg,
      );
    } else if (sub.isExpired) {
      const msg = this.translateService.instant('ORDER-EXPIRED');
      this.messageBoxService.showTextMessage(
        this.messageBoxService.MODE_TYPE.OK,
        this.translateService.instant('WARNING'),
        msg,
      );
    }
  }

  openStats(sub: SubscriptionDto) {
    if (sub && sub.order) {
      let url = window.document.location.ancestorOrigins[0];
      switch (sub.order.licenseType) {
        case 's':
          const encodedUserEmail = btoa(sub.access.admin.eMail);
          const encodedUserName = btoa(sub.access.admin.name);
          url += '/user-stats/' + encodedUserEmail + '/' + encodedUserName;
          this.tracker.trackEvent('Stats', 'Open-User-Stats', 'Open user stats page');
          break;
        case 'm':
        case 'd':
          url += '/multi-stats/' + sub.order.subscriptionId;
          this.tracker.trackEvent('Stats', 'Open-Multi-Stats', 'Open multi stats page');
          break;
      }
      window.open(url, '_blank');
    }
  }

  resetSearch() {
    this.pageIndex.set(0);
    this.accessConfigService.pageEvent.set(undefined);

    this.searchForm?.get('searchText')?.setValue('');
    this.loadData('');
  }

  onSubmit() {
    this.pageIndex.set(0);
    this.accessConfigService.pageEvent.set(undefined);
    this.search();
  }

  search() {
    const textSearch = this.searchForm?.get('searchText')?.value;
    this.loadData(textSearch);
  }

  getFindsDescription(finds: any[]) {
    let s = '';
    if (finds && finds.length > 0) {
      for (let i = 0; i < finds.length; i++) {
        const find = finds[i];
        if (s !== '') s += ', ';
        s += this.translateService.instant(find.where);
        if (find.pos >= 0) s += find.pos + 1;
      }
    }
    return this.translateService.instant('FOUND_IN') + ' ' + s;
  }

  renewSubscription(subscription: SubscriptionDto) {
    let ok = false;
    if (
      subscription &&
      subscription.order &&
      subscription.order.subscriptionId &&
      subscription.order.licenseType
    ) {
      try {
        this.tracker.trackEvent('Abbonamenti', 'Click renew', 'RenewSubscription');
      } catch (e: any) {
        logger.error('matomo error ' + e.message);
      }

      if (subscription.order.origin === 'doposcuola') {
        ok = true;
        this.messageBoxService.showRenewAfterSchoolContactUs();
      } else if (
        (subscription.order.origin !== 'manual' &&
          subscription.order.origin !== 'buy' &&
          subscription.order.origin !== 'demo') ||
        (subscription.order.licenseType === 's' && subscription.order.planId === 'maps') ||
        subscription.order.licenseType !== 's'
      ) {
        ok = true;
        this.messageBoxService.showRenewContactUs();
      } else {
        const productId = this.getSMXProductId(
          subscription.order.origin,
          subscription.order.licenseType,
          subscription.order.domainLics,
        );
        if (productId) {
          ok = true;
          const prefix = environment.production ? 'www' : 'test';
          const url = `https://${prefix}.anastasis.it/acquisto/smxstudio/${productId}?ana-smx-subscription-id=${subscription.order.subscriptionId}`;
          window.open(url, '_blank');
        }
      }
    }
    if (!ok) {
      this.confirmationService.confirm(
        this.translateService.instant(extract('WARNING')),
        this.translateService.instant(extract('ACCESSCONFIG_RENEW_INVALID_ORDER')),
        this.translateService.instant(extract('BUTTON_OK')),
        '',
      );
    }
  }

  buyAiCredits(subscription: SubscriptionDto) {
    const msg = new BuyCreditsMessage(
      this.moduleStateService.guid,
      subscription.order.aiCredits,
      subscription.order.subscriptionId,
    );
    this.moduleMessageService.sendMessage(msg);
  }

  deleteSubscription(sub: SubscriptionDto) {
    this.confirmationService
      .confirm(
        this.translateService.instant(extract('WARNING')),
        this.translateService
          .instant(extract('DELETE_CONFIRM'))
          .replace(/§orderId§/g, sub.order.subscriptionId),
        this.translateService.instant(extract('BUTTON_OK')),
        this.translateService.instant(extract('CANCEL')),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          if (result) {
            this.loaderService.startLoading('generic', 'PLEASE_WAIT');
            this.apiService
              .deleteSubscription(sub.order.subscriptionId)
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe({
                next: (_result: any) => {
                  this.loaderService.stopLoading('generic');
                  if (_result.result) {
                    this.confirmationService
                      .confirm(
                        '',
                        this.translateService.instant(extract('ACCESSCONFIG_MSG_DELETE_OK')),
                        this.translateService.instant(extract('BUTTON_OK')),
                        '',
                      )
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe(() => this.reloadData());
                  } else {
                    this.confirmationService
                      .confirm(
                        '',
                        this.translateService.instant(extract('ACCESSCONFIG_MSG_DELETE_FAIL')),
                        this.translateService.instant(extract('BUTTON_OK')),
                        '',
                      )
                      .pipe(takeUntilDestroyed(this.destroyRef))
                      .subscribe();
                  }
                },
                error: (error: any) => {
                  this.loaderService.stopLoading('generic');
                  logger.error(
                    this.translateService.instant('ACCESSCONFIG_MSG_DELETE_FAIL') +
                    '\n' +
                    error.message,
                  );
                  this.confirmationService
                    .confirm(
                      '',
                      this.translateService.instant(extract('ACCESSCONFIG_MSG_DELETE_FAIL')),
                      this.translateService.instant(extract('BUTTON_OK')),
                      '',
                    )
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe();
                },
              });
          }
        },
        error: (error: any) => {
          this.loaderService.stopLoading('generic');
          logger.error(error);
        },
      });
  }

  private getSMXProductId(origin: string, licenseType: string, domainLics: number): string {
    return licenseType === 's' ? 'privati-1anno' : '';
  }

  public onOrderTypeFilterPressed(t: OrderFilterType) {
    this.activeOrderFilter.set(t);
    this.onSubmit();
  }
}
